import { useCallback, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { ErroRespostaHttp } from '../../../shared/api/http';
import { alterarJogador, excluirJogador, listarJogadores } from '../../jogadores/api/jogadores';
import { comandarEstacao, excluirGrupo } from '../api/operacao';
import type { AcaoDeEstacao } from '../api/operacao';
import type { CampoDeJogador, Jogador, JogadorAlterado, ValorDeJogador } from '../../jogadores/api/jogadores';
import type { AvisoDaTela } from '../../participantes/ui/Aviso';
import { SITUACAO } from './acoesDaLinha';
import type { AcaoDaLinha } from './acoesDaLinha';

/** A janela de confirmação aberta por cima do modal: no máximo uma por vez. */
export type Janela =
    | { tipo: 'situacao'; jogador: Jogador; acao: 'inativar' | 'reativar' }
    | { tipo: 'lider'; jogador: Jogador }
    | { tipo: 'desfazer'; jogador: Jogador }
    | { tipo: 'alocar'; jogador: Jogador }
    | { tipo: 'excluir'; jogador: Jogador }
    | { tipo: 'pontuacao'; jogador: Jogador }
    | { tipo: 'excluirGrupo' }
    | { tipo: 'estacao'; acao: AcaoDeEstacao; fase: string }
    | { tipo: 'iniciar'; fase: string; rotulo: string; acao: 'iniciar' | 'reiniciar' };

/** O texto que o servidor mandou no erro (`mensagem`), ou o da tela. */
function mensagemDoServidor(erro: unknown, padrao: string) {
    if (erro instanceof ErroRespostaHttp && typeof erro.corpo?.mensagem === 'string' && erro.corpo.mensagem) {
        return erro.corpo.mensagem;
    }
    return padrao;
}

const SUCESSO_DA_ESTACAO: Record<AcaoDeEstacao, string> = {
    reiniciar: 'Estação reiniciada com sucesso.',
    finalizar: 'Estação finalizada com sucesso.',
};

const FALHA_DA_ESTACAO: Record<AcaoDeEstacao, string> = {
    reiniciar: 'Não foi possível reiniciar a estação.',
    finalizar: 'Não foi possível finalizar a estação.',
};

/** O que o servidor disse: o `motivo` do jogo na recusa, a `mensagem` nos demais; senão o texto da ação. */
function textoDaFalhaDaEstacao(erro: unknown, acao: AcaoDeEstacao) {
    if (erro instanceof ErroRespostaHttp && erro.corpo && typeof erro.corpo === 'object') {
        const { codigo, motivo, mensagem } = erro.corpo;
        if (codigo === 'comando_recusado' && typeof motivo === 'string' && motivo) return motivo;
        if (typeof mensagem === 'string' && mensagem) return mensagem;
    }
    return FALHA_DA_ESTACAO[acao];
}

/**
 * Falha do "Iniciar": o `504` não é "falhou" — o comando pode ter valido, e repetir no escuro move
 * o horário de abertura (`DP-OP-004`); na recusa vale o `motivo` do jogo.
 */
function textoDaFalhaDoInicio(erro: unknown, rotulo: string, acao: 'iniciar' | 'reiniciar') {
    if (erro instanceof ErroRespostaHttp) {
        if (erro.status === 504) {
            return `O jogo não respondeu a tempo. ${rotulo} pode ter valido: confira o resumo antes de repetir.`;
        }
        const { codigo, motivo, mensagem } = (erro.corpo ?? {}) as Record<string, unknown>;
        if (codigo === 'comando_recusado' && typeof motivo === 'string' && motivo) return motivo;
        if (typeof mensagem === 'string' && mensagem) return mensagem;
    }
    return `Não foi possível ${acao} ${rotulo}.`;
}

const FALHA: Record<AcaoDaLinha, string> = {
    inativar: 'Não foi possível inativar o participante.',
    reativar: 'Não foi possível reativar o participante.',
    lider: 'Não foi possível definir o(a) líder.',
    desfazer: 'Não foi possível desfazer a liderança.',
    alocar: 'Não foi possível alocar o participante.',
    pontuacao: 'Não foi possível abrir a pontuação.',
    excluir: 'Não foi possível excluir o participante.',
};

interface Entrada {
    turmaId: string;
    grupoId: string;
    /** Como o modal chama o grupo ("Grupo G02"): é o destino do "Alocar". */
    grupoRotulo: string;
    csrfToken: string;
    /** Os membros do grupo aberto (a tabela). */
    membros: Jogador[] | null;
    setMembros: Dispatch<SetStateAction<Jogador[] | null>>;
    /** Todos os jogadores da turma: onde o "Alocar" procura a matrícula (`RN-RA-020`). */
    todos: Jogador[];
    setTodos: Dispatch<SetStateAction<Jogador[]>>;
    /** A estação atual do cabeçalho (`fase_atual.fase`); `null` quando não há comando possível (`RA6`). */
    estacaoAtual: string | null;
    /** Depois de um comando de estação que valeu: o modal relê o card, o cabeçalho e o resumo. */
    aoMudarEstacao: () => void;
    /** Os membros do grupo mudaram (exclusão): a Operação relê o card, que conta participantes. */
    aoMudarMembros: () => void;
    /** O grupo foi excluído: quem abriu o modal o fecha e avisa (`RN-RA-036`). */
    aoExcluirGrupo: (rotulo: string) => void;
}

/**
 * O fluxo das ações do modal do grupo (`RA4`, `RA5`): menu, confirmação, `PATCH` e aviso. O estado
 * das listas continua do modal; o hook só o atualiza com o que o servidor respondeu.
 */
export function useAcoesDoGrupo({
    turmaId, grupoId, grupoRotulo, csrfToken, membros, setMembros, todos, setTodos, estacaoAtual, aoMudarEstacao,
    aoMudarMembros, aoExcluirGrupo,
}: Entrada) {
    const [menuAberto, setMenuAberto] = useState<string | null>(null);
    const [confirmacao, setConfirmacao] = useState<Janela | null>(null);
    const [processando, setProcessando] = useState(false);
    const [aviso, setAviso] = useState<AvisoDaTela | null>(null);
    const fecharAviso = useCallback(() => setAviso(null), []);
    const fecharMenu = useCallback(() => setMenuAberto(null), []);
    const fecharConfirmacao = useCallback(() => setConfirmacao(null), []);
    const alternarMenu = useCallback(
        (id: string) => setMenuAberto((aberto) => (aberto === id ? null : id)),
        [],
    );
    // Esc e Tab pertencem ao que estiver por cima (menu ou popup): o modal só reage quando não há nada
    // aberto. Lidos em refs porque os ouvintes de Esc rodam todos no mesmo evento, antes de nova renderização.
    const porCima = useRef(false);
    porCima.current = menuAberto !== null || confirmacao !== null;
    const popupAberto = useRef(false);
    popupAberto.current = confirmacao !== null;

    /**
     * Põe na tabela o que o servidor devolveu: a linha alterada (que entra, se foi alocada para cá),
     * o rebaixado na troca de líder e o `novo_lider` sorteado (ou assumido) pelo servidor (`RN-RA-041`).
     */
    function aplicar(resposta: JogadorAlterado) {
        setMembros((lista) => {
            const base = lista ?? [];
            const atualizada = base.map((membro) => {
                if (membro.id === resposta.jogador.id) return resposta.jogador;
                if (resposta.rebaixado && membro.login === resposta.rebaixado) return { ...membro, lider: false };
                if (resposta.novo_lider && membro.login === resposta.novo_lider) return { ...membro, lider: true };
                return membro;
            });
            const jaEstava = base.some((membro) => membro.id === resposta.jogador.id);
            return jaEstava || resposta.jogador.grupo_id !== grupoId ? atualizada : [...atualizada, resposta.jogador];
        });
        setTodos((lista) => lista.map((membro) => (membro.id === resposta.jogador.id ? resposta.jogador : membro)));
    }

    /** `409`: outro operador mudou o campo antes; a linha passa a mostrar o valor atual e nada é reenviado. */
    function aplicarValorAtual(jogador: Jogador, campo: CampoDeJogador, atualNoServidor: unknown) {
        const mudar = (membro: Jogador): Jogador => {
            if (membro.id !== jogador.id) return membro;
            if (campo === 'lider') return { ...membro, lider: atualNoServidor === true };
            if (campo === 'grupo_id') return { ...membro, grupo_id: atualNoServidor === null ? null : String(atualNoServidor) };
            return { ...membro, situacao: atualNoServidor as Jogador['situacao'] };
        };
        // Quem saiu deste grupo por outro operador não pertence mais à tabela dele; o `alocar` não
        // mexe nela (o alocado nem está nela).
        if (campo !== 'grupo_id') setMembros((lista) => (lista ?? []).map(mudar));
        setTodos((lista) => lista.map(mudar));
    }

    /** Relê a turma; se a leitura falhar, as listas ficam com o que já mostravam. */
    async function reler() {
        try {
            const resposta = await listarJogadores(turmaId);
            setTodos(resposta.jogadores);
            setMembros(resposta.jogadores.filter((membro) => membro.grupo_id === grupoId));
        } catch {
            // Sem a releitura, vale o que a resposta do PATCH já aplicou.
        }
    }

    function nomeDe(login: string) {
        const achado = [...todos, ...(membros ?? [])].find((membro) => membro.login === login);
        return achado?.nome ?? login;
    }

    function sucessoDe(acao: AcaoDaLinha) {
        return {
            inativar: 'Participante inativado(a) com sucesso.',
            reativar: 'Participante reativado(a) com sucesso.',
            lider: 'Novo(a) líder definido(a) com sucesso.',
            desfazer: 'Liderança desfeita.',
            alocar: `Participante transferido(a) para o ${grupoRotulo}.`,
            pontuacao: '',
            excluir: 'Participante excluído(a) definitivamente.',
        }[acao];
    }

    /**
     * Excluir definitivamente (`RA8`, `RN-RA-032`): a linha sai da tabela e da turma; se era o líder,
     * o servidor já sorteou outro (`novo_lider`) e a tabela é relida, para nunca mostrar dois líderes.
     */
    async function excluir(jogador: Jogador) {
        if (processando) return;
        setProcessando(true);
        let precisaReler = jogador.lider;
        try {
            const resposta = await excluirJogador({ id: jogador.id, turmaId, csrfToken });
            const marcar = (lista: Jogador[]) =>
                lista
                    .filter((membro) => membro.id !== jogador.id)
                    .map((membro) => (resposta.novo_lider && membro.login === resposta.novo_lider ? { ...membro, lider: true } : membro));
            setMembros((lista) => marcar(lista ?? []));
            setTodos((lista) => marcar(lista));
            const quem = resposta.novo_lider ? ` ${nomeDe(resposta.novo_lider)} é o(a) novo(a) líder.` : '';
            setAviso({ tipo: 'sucesso', texto: `${sucessoDe('excluir')}${quem}` });
            aoMudarMembros();
        } catch (erro) {
            if (erro instanceof ErroRespostaHttp && erro.status === 404) {
                setAviso({ tipo: 'erro', texto: 'Este participante já não está na turma.' });
                precisaReler = true;
            } else {
                setAviso({ tipo: 'erro', texto: mensagemDoServidor(erro, FALHA.excluir) });
            }
        } finally {
            setProcessando(false);
            setConfirmacao(null);
        }
        if (precisaReler) await reler();
    }

    /** Excluir o grupo (`RA8`, `RN-RA-034`): com histórico o servidor recusa, e o modal continua aberto. */
    async function excluirOGrupo() {
        if (processando) return;
        setProcessando(true);
        let valeu = false;
        try {
            await excluirGrupo({ turmaId, grupoId, csrfToken });
            valeu = true;
        } catch (erro) {
            setAviso({ tipo: 'erro', texto: mensagemDoServidor(erro, 'Não foi possível excluir o grupo.') });
        } finally {
            setProcessando(false);
            setConfirmacao(null);
        }
        if (valeu) aoExcluirGrupo(grupoRotulo);
    }

    function pedidoDe(jogador: Jogador, acao: AcaoDaLinha): [CampoDeJogador, ValorDeJogador, ValorDeJogador] {
        if (acao === 'lider') return ['lider', true, false];
        if (acao === 'desfazer') return ['lider', false, true];
        if (acao === 'alocar') return ['grupo_id', grupoId, jogador.grupo_id];
        return ['situacao', acao === 'inativar' ? 'inativo' : 'ativo', jogador.situacao];
    }

    function motivoDo409(campo: CampoDeJogador, acao: AcaoDaLinha, atualNoServidor: unknown) {
        if (acao === 'desfazer') return 'já não é líder do grupo';
        if (campo === 'lider') return 'já é o(a) líder do grupo';
        if (campo === 'grupo_id') {
            return atualNoServidor === null ? 'já está sem grupo' : `já está no Grupo ${String(atualNoServidor)}`;
        }
        return `já está ${SITUACAO[atualNoServidor as Jogador['situacao']] ?? 'com outra situação'}`;
    }

    async function enviar(jogador: Jogador, acao: AcaoDaLinha) {
        const [campo, valor, anterior] = pedidoDe(jogador, acao);
        // Mexe na liderança: troca de líder, desfazer, inativar o líder, alocar (os dois grupos) ou o
        // servidor ter feito alguém assumir. Relida, a tabela nunca mostra dois líderes (`RN-RA-014`).
        let mexeuNaLideranca = campo === 'lider' || acao === 'alocar' || (acao === 'inativar' && jogador.lider);
        setProcessando(true);
        try {
            const resposta = await alterarJogador({
                id: jogador.id, turmaId, campo, valor, valorAnterior: anterior, novoLider: null, csrfToken,
            });
            aplicar(resposta);
            const quem = resposta.novo_lider ? ` ${nomeDe(resposta.novo_lider)} é o(a) novo(a) líder.` : '';
            mexeuNaLideranca = mexeuNaLideranca || resposta.novo_lider !== null;
            setAviso({ tipo: 'sucesso', texto: `${sucessoDe(acao)}${quem}` });
        } catch (erro) {
            if (erro instanceof ErroRespostaHttp && erro.status === 409 && erro.corpo?.codigo === 'valor_desatualizado') {
                aplicarValorAtual(jogador, campo, erro.corpo.valor_atual);
                const agora = motivoDo409(campo, acao, erro.corpo.valor_atual);
                setAviso({ tipo: 'erro', texto: `Outro operador alterou este participante antes: ele(a) ${agora}. Nada foi gravado.` });
            } else if (erro instanceof ErroRespostaHttp && erro.status === 409 && erro.corpo?.codigo === 'sem_substituto_ativo') {
                // A tela estava desatualizada: o último outro ativo saiu. A releitura tira o item do menu.
                setAviso({ tipo: 'erro', texto: 'Não há outro participante ativo no grupo para assumir a liderança.' });
            } else {
                setAviso({ tipo: 'erro', texto: FALHA[acao] });
            }
        } finally {
            setProcessando(false);
            setConfirmacao(null);
        }
        if (mexeuNaLideranca) await reler();
    }

    /** Abre o popup de Reiniciar/Finalizar; nada é enviado até a confirmação (`AC-RA-047`). */
    function escolherEstacao(acao: AcaoDeEstacao) {
        // A fase fica na janela: é a estação que o operador viu, mesmo que a releitura da Operação a
        // troque com o popup aberto (o jogo responde `409 estacao_mudou`, `DP-RA-040`).
        if (estacaoAtual && !processando) setConfirmacao({ tipo: 'estacao', acao, fase: estacaoAtual });
    }

    /**
     * Reiniciar/Finalizar a estação do cabeçalho (`RA6`). Nunca repete sozinho: no `504` o comando
     * pode ter valido, e quem decide repetir é o operador.
     */
    async function enviarEstacao(acao: AcaoDeEstacao, fase: string) {
        if (processando) return;
        setProcessando(true);
        let valeu = false;
        try {
            await comandarEstacao({ turmaId, grupoId, fase, acao, csrfToken });
            valeu = true;
            setAviso({ tipo: 'sucesso', texto: SUCESSO_DA_ESTACAO[acao] });
        } catch (erro) {
            setAviso({ tipo: 'erro', texto: textoDaFalhaDaEstacao(erro, acao) });
        } finally {
            setProcessando(false);
            setConfirmacao(null);
        }
        if (valeu) aoMudarEstacao();
    }

    function escolherIniciar(fase: string, rotulo: string, acao: 'iniciar' | 'reiniciar' = 'iniciar') {
        if (!processando) setConfirmacao({ tipo: 'iniciar', fase, rotulo, acao });
    }

    /**
     * Coloca o grupo na fase: o painel abre a janela e inicia a estação pela rota do facilitador, e a
     * estação `iniciado` leva os tablets do Aguarde para a fase. Nunca repete sozinho (`DP-OP-004`).
     */
    async function enviarIniciar(fase: string, rotulo: string, acao: 'iniciar' | 'reiniciar' = 'iniciar') {
        if (processando) return;
        setProcessando(true);
        let valeu = false;
        try {
            await comandarEstacao({ turmaId, grupoId, fase, acao, csrfToken });
            valeu = true;
            const feito = acao === 'iniciar' ? 'iniciado' : 'reiniciado';
            setAviso({ tipo: 'sucesso', texto: `${rotulo} ${feito} no ${grupoRotulo}.` });
        } catch (erro) {
            setAviso({ tipo: 'erro', texto: textoDaFalhaDoInicio(erro, rotulo, acao) });
        } finally {
            setProcessando(false);
            setConfirmacao(null);
        }
        if (valeu) aoMudarEstacao();
    }

    function escolher(jogador: Jogador, acao: AcaoDaLinha) {
        setMenuAberto(null);
        if (acao === 'pontuacao') setConfirmacao({ tipo: 'pontuacao', jogador });
        else if (acao === 'excluir') setConfirmacao({ tipo: 'excluir', jogador });
        else if (acao === 'desfazer') setConfirmacao({ tipo: 'desfazer', jogador });
        else if (acao === 'inativar' || acao === 'reativar') setConfirmacao({ tipo: 'situacao', jogador, acao });
        // Sem líder no grupo não há quem substituir: atribui direto (`DP-RA-034`, RI-MA-04).
        else if ((membros ?? []).some((membro) => membro.lider)) setConfirmacao({ tipo: 'lider', jogador });
        else void enviar(jogador, 'lider');
    }

    /**
     * "Alocar" do modal (`RN-RA-020`): procura a matrícula na lista da turma, sem diferenciar caixa.
     * Só abre o popup quando é de outro grupo; os outros casos avisam, sem requisição de escrita.
     */
    function buscarParaAlocar(texto: string): boolean {
        const matricula = texto.trim().toLowerCase();
        if (!matricula) return false;
        const achado = todos.find((membro) => membro.login.toLowerCase() === matricula);
        if (!achado) {
            setAviso({ tipo: 'erro', texto: 'Matrícula não encontrada nesta turma.' });
            return false;
        }
        if (achado.grupo_id === grupoId) {
            setAviso({ tipo: 'erro', texto: 'Este participante já está neste grupo.' });
            return false;
        }
        setConfirmacao({ tipo: 'alocar', jogador: achado });
        return true;
    }

    return {
        menuAberto, alternarMenu, fecharMenu,
        confirmacao, fecharConfirmacao, processando,
        aviso, fecharAviso,
        escolher, enviar, buscarParaAlocar, escolherEstacao, enviarEstacao, escolherIniciar, enviarIniciar,
        excluir, excluirOGrupo,
        escolherExcluirGrupo: () => {
            if (!processando) setConfirmacao({ tipo: 'excluirGrupo' });
        },
        porCima, popupAberto,
    };
}
